import { useState, useMemo, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Paper, Grid } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import BookingSectionOccupants from '../../public/booking/BookingSectionOccupants';
import BookingSectionPrice from '../../public/booking/BookingSectionPrice';
import BookingOperateDialog from '../../public/booking/BookingOperateDialog';
import DataphonePaymentDialog from '../../../components/dialogs/DataphonePaymentDialog';
import POSStripePaymentDialog from '../../../components/stripe/POSStripePaymentDialog';
import constants from '../../../config/constants';
import useGameService from '../../../services/GameService';
import {
    AppContext,
    SttButton,
    SttFullDialog,
    SttLabelForm,
} from '../../../sporttia/all';
import type { Occupant } from '../../../types/models/Occupant';
import { getPath } from '../../Pages';
import type { Error } from '../../../types/models/Error';
import CashdroControlDialog from '../../../components/dialogs/CashdroControlDialog';
import { PosPaymentParams } from '../../../types/payment';

const validPaymentForms = [
    '',
    'CASH',
    'PURSE',
    'DATAFONO',
    'DATAFONO_CONNECTED',
    'TPV',
    'BANK',
    'FREE',
    'RECEIPT',
    'ZITYCARD',
    'CASHDRO',
];

export default function AddPlayerSC() {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const cxt = useContext(AppContext)!;

    const { useGetGame, usePostGamePlayer } = useGameService();
    const { data: gameResponse } = useGetGame(parseInt(id || '0'));
    const { game } = useMemo(
        () => ({
            game: gameResponse?.game,
        }),
        [gameResponse],
    );
    const [occupants, setOccupants] = useState<Occupant[]>([]);
    const [openOperate, setOpenOperate] = useState(false);
    const [posPaymentParams, setPosPaymentParams] =
        useState<PosPaymentParams<unknown> | null>(null);
    const price = useMemo(
        () =>
            occupants.reduce<number>((acc: any, occupant: any) => {
                if (occupant.selectedRate) {
                    return acc + Number(occupant.selectedRate.total) || 0;
                }
                return acc;
            }, 0),
        [occupants],
    );

    const CASHDRO = cxt?.sc?.cashdroConnected;
    const [cashdroPayment, setCashdroPayment] = useState(false);
    const [cashdroPaymentAmount, setCashdroPaymentAmount] = useState(0);

    const gameId = game?.id ?? 0;
    const postPlayerMutation = usePostGamePlayer(gameId, {
        onSuccess: (response: any) => {
            setOpenOperate(false);
            if (
                response.tpv &&
                response.tpv.payment?.paymentForm ===
                    constants.payment.paymentForms.dataphoneConnected.name
            ) {
                setPosPaymentParams({
                    amount: response.tpv.price,
                    concept: response.tpv.concept,
                    idTpv: response.tpv.id,
                    sc: {
                        id: cxt?.sc?.id,
                        scName: cxt?.sc?.name,
                    },
                    returnParams: undefined,
                } as any);
            } else if (response.tpv && response.tpv.id) {
                navigate(getPath('tpvSwitcher', { id: response.tpv.id }));
            } else {
                navigate(`/games/${gameId}/sc`);
            }
        },
        onError: (err: unknown) => {
            const apiErr = err as Error;
            const errorMsg = apiErr?.error?.msg || t('Error en la inscripción');
            cxt?.showMessage('E', errorMsg);
        },
    });

    const handleBook = (player: Occupant, paymentForm: string) => {
        if (!game?.id) return;
        postPlayerMutation.mutate({
            params: {
                userId: player.user.id,
                paymentForm,
                rates: player.selectedRate
                    ? player.selectedRate.prices.map((ratePrice: any) => ({
                          id: ratePrice.id,
                          duration: ratePrice.duration,
                      }))
                    : undefined,
                idBoleto: player.selectedBoleto?.id,
            },
        });
    };

    const handleConfirmPlayer = (
        paymentForm: string | { paymentForm?: string },
    ) => {
        const player = occupants[0];
        if (!player) return;

        let paymentFormValue: string =
            typeof paymentForm === 'object'
                ? (paymentForm.paymentForm ?? '')
                : (paymentForm ?? '');

        if (CASHDRO && paymentFormValue === 'CASHDRO') {
            setCashdroPayment(true);
            setCashdroPaymentAmount(price);
            return;
        }

        if (!validPaymentForms.includes(paymentFormValue)) {
            paymentFormValue = '';
        }

        handleBook(player, paymentFormValue);
    };

    if (!game) return null;

    return (
        <SttFullDialog
            open
            title={t('addPlayerToGame')}
            onClose={() => navigate(-1)}
        >
            <Container maxWidth="md">
                <Paper style={{ marginBottom: 24, padding: 20 }}>
                    <Box mt={3}>
                        <Paper>
                            <Box p={3}>
                                <Grid
                                    container
                                    spacing={3}
                                    justifyContent="center"
                                >
                                    <SttLabelForm
                                        grid
                                        md={5}
                                        title={t('Date')}
                                        text={format(
                                            parseISO(game.ini),
                                            'EEEE, d MMMM yyyy',
                                            { locale: getDateLocale() },
                                        )}
                                    />

                                    <SttLabelForm
                                        grid
                                        md={7}
                                        title={t('Facility')}
                                        text={`${game.facility.sc.name}, ${game.facility.name}`}
                                    />
                                </Grid>

                                <Grid
                                    container
                                    spacing={3}
                                    justifyContent="center"
                                >
                                    <SttLabelForm
                                        grid
                                        md={5}
                                        title={t('Time')}
                                        text={`${format(parseISO(game.ini), 'HH:mm')} - ${format(parseISO(game.end), 'HH:mm')}`}
                                    />

                                    <SttLabelForm
                                        grid
                                        md={7}
                                        title={t('Sport')}
                                        text={game.facility.sport.name}
                                    />
                                </Grid>
                            </Box>
                        </Paper>
                    </Box>
                    <BookingSectionOccupants
                        timeini={game.ini}
                        timeend={game.end}
                        piece={game}
                        column={{ facility: game.facility }}
                        sc={game.facility.sc}
                        rateType={constants.rate.types.individual}
                        setRateType={undefined}
                        ImSCOwner
                        occupants={occupants}
                        setOccupants={setOccupants}
                        isForGame
                    />
                    <Box mt={2}>
                        <BookingSectionPrice
                            price={price}
                            sc={game.facility.sc}
                            onChangePrice={undefined}
                            modify={false}
                        />
                    </Box>
                    <Box mt={4} mb={2}>
                        <SttButton
                            loading={postPlayerMutation?.isPending}
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                const player = occupants[0];
                                if (!player) return;
                                if (player.selectedBoleto || price <= 0) {
                                    handleBook(player, 'FREE');
                                } else {
                                    setOpenOperate(true);
                                }
                            }}
                        >
                            {t('SignUp')}
                        </SttButton>
                    </Box>
                    <BookingOperateDialog
                        open={openOperate}
                        staticMsg=""
                        mship={occupants[0]?.mship}
                        rateType={constants.rate.types.individual}
                        onClose={() => setOpenOperate(false)}
                        finalPrice={price}
                        onBook={handleConfirmPlayer}
                        sc={game.facility.sc}
                        fares={undefined}
                    />
                </Paper>

                {cxt?.sc?.sporttiaStripeLocationId &&
                posPaymentParams !== null ? (
                    <POSStripePaymentDialog
                        paymentData={posPaymentParams}
                        onClose={() => setPosPaymentParams(null)}
                        onSuccess={() => {
                            navigate(`/games/${game.id}/sc`);
                        }}
                    />
                ) : (
                    <DataphonePaymentDialog
                        open={posPaymentParams !== null}
                        paymentData={posPaymentParams}
                        onClose={() => setPosPaymentParams(null)}
                        onSuccess={() => {
                            navigate(`/games/${game.id}/sc`);
                        }}
                    />
                )}
            </Container>

            {CASHDRO && cashdroPayment && (
                <CashdroControlDialog
                    open={cashdroPayment}
                    amount={cashdroPaymentAmount}
                    onClose={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                    }}
                    onSuccess={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                        const player = occupants[0];
                        if (player) handleBook(player, 'CASHDRO');
                    }}
                />
            )}
        </SttFullDialog>
    );
}
